import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Type, spacer8 } from '@spotify-internal/encore-web/core';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { CopyToGreenCheckAnimation } from '../CopyToGreenCheckAnimation';
import { TextLinkToExternal } from '../TextLinkToExternal';
import { PillInfoWithIcon } from './PillInfoWithIcon';

const IconContainer = styled.div`
  margin-right: ${spacer8};
`;

type PillInfoWithHrefAndCopyProps = {
  gaEventCategory: EVENT_CATEGORY;
  label: string;
  value: string;
  href?: string;
  hasSubduedValueTextColor?: boolean;
  hasBackgroundColor?: boolean;
  copyTooltipPopupText?: string;
  tagText?: string;
};

export const PillInfoWithHrefAndCopy: FunctionComponent<
  PillInfoWithHrefAndCopyProps
> = props => {
  const {
    label,
    value,
    href,
    hasSubduedValueTextColor,
    hasBackgroundColor,
    copyTooltipPopupText,
    gaEventCategory,
    tagText,
  } = props;

  const t = useT();

  return (
    <PillInfoWithIcon
      label={label}
      value={
        !!href ? (
          <TextLinkToExternal
            href={href}
            valueText={value || ''}
            valueTextColor="textBrightAccent"
            hasTextUnderline={false}
            handleClick={undefined}
          />
        ) : (
          <Type
            variant="viola"
            semanticColor={hasSubduedValueTextColor ? 'textSubdued' : undefined}
          >
            {value}
          </Type>
        )
      }
      tagText={tagText}
      icon={
        <IconContainer>
          <CopyToGreenCheckAnimation
            withPortal
            hasHoverBackground
            label={`Copy ${label}`}
            tooltipText={copyTooltipPopupText}
            copyText={value}
            snackbarText={t(
              'atlas.snackbars.copied_to_clipboard',
              'Copied to clipboard',
              'Snackbar message to indicate copied to clipboard',
            )}
            screenReaderText={copyTooltipPopupText}
            handleClick={() =>
              sendEvent({
                eventCategory: gaEventCategory,
                eventAction: EVENT_ACTION.CLICK,
                eventLabel: `${label} copied`,
                product: GA_PRODUCT_CATALOG,
              })
            }
          />
        </IconContainer>
      }
      backgroundColor={hasBackgroundColor ? 'backgroundHighlight' : undefined}
      subduedValueTextColor={
        hasSubduedValueTextColor ? 'textSubdued' : undefined
      }
    />
  );
};

export default PillInfoWithHrefAndCopy;
